import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Shield } from "lucide-react";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const Gauge = ({ label, value, warning, danger, unit = "%", testId }) => {
  // value: 0-100 usage percentage. Gauge color: green<warning, amber<danger, red>=danger
  const pct = clamp(value, 0, 100);
  let color = "#10b981";
  if (pct >= danger) color = "#ef4444";
  else if (pct >= warning) color = "#f59e0b";

  const radius = 34;
  const circ = 2 * Math.PI * radius;
  const offset = circ * (1 - pct / 100);

  return (
    <div className="flex flex-col items-center gap-1" data-testid={testId}>
      <svg width="80" height="80" viewBox="0 0 80 80" className="-rotate-90">
        <circle cx="40" cy="40" r={radius} stroke="#1f2937" strokeWidth="6" fill="none" />
        <circle
          cx="40" cy="40" r={radius}
          stroke={color} strokeWidth="6" fill="none" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 500ms ease-out, stroke 300ms" }}
        />
      </svg>
      <div className="-mt-14 font-mono font-bold text-sm" style={{ color }}>{pct.toFixed(1)}{unit}</div>
      <div className="mt-6 vx-label text-center">{label}</div>
    </div>
  );
};

export default function RiskRadar() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const load = () => api.get("/portfolio/risk-status").then((r) => setStatus(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  if (!status) return (
    <div className="vx-card p-5 h-full" data-testid="risk-radar-loading">
      <div className="vx-label mb-4">Risk Radar</div>
      <div className="text-xs text-[#6b7280] font-mono">Loading…</div>
    </div>
  );

  // For daily loss we show USAGE = |daily_pnl_pct| / daily_loss_limit_pct * 100 when loss, else 0
  const dailyLimit = status.limits?.daily_loss_limit_pct || 3.0;
  const dailyLossUsage = status.daily_pnl_pct < 0 ? (Math.abs(status.daily_pnl_pct) / dailyLimit) * 100 : 0;
  const ddUsage = (status.drawdown_pct / (status.limits?.max_drawdown_pct || 10)) * 100;
  const marginUsage = (status.margin_utilization_pct / (status.limits?.margin_utilization_max_pct || 50)) * 100;

  return (
    <div className="vx-card p-5" data-testid="risk-radar">
      <div className="flex items-center justify-between mb-4">
        <div className="vx-label">Risk Radar</div>
        <Shield className="w-3.5 h-3.5 text-[#3b82f6]" />
      </div>
      <div className="grid grid-cols-3 gap-2 justify-items-center">
        <Gauge label="Daily Loss" value={dailyLossUsage} warning={60} danger={90} testId="gauge-daily-loss" />
        <Gauge label="Drawdown" value={ddUsage} warning={60} danger={90} testId="gauge-drawdown" />
        <Gauge label="Margin" value={marginUsage} warning={60} danger={90} testId="gauge-margin" />
      </div>
      <div className="mt-4 pt-3 border-t border-[#1f2937] text-[10px] font-mono text-[#9ca3af] space-y-1">
        <div className="flex justify-between"><span>Daily P&L</span><span className={status.daily_pnl >= 0 ? "text-[#10b981]" : "text-[#ef4444]"}>${status.daily_pnl?.toFixed(2)} ({status.daily_pnl_pct?.toFixed(2)}%)</span></div>
        <div className="flex justify-between"><span>Drawdown</span><span>{status.drawdown_pct?.toFixed(2)}%</span></div>
        <div className="flex justify-between"><span>Positions</span><span>{status.positions_count}</span></div>
      </div>
    </div>
  );
}
